import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Brain, Truck, TicketCheck, FileText, DollarSign, AlertTriangle, TrendingUp, Users, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useFilial } from "../context/FilialContext";

const fmt = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const KpiCard = ({ icon: Icon, label, value, hint, color = "#1B3A6B", testId }) => (
  <Card
    data-testid={testId}
    className="p-5 border-l-4 hover:shadow-md transition-shadow"
    style={{ borderLeftColor: color }}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          {label}
        </div>
        <div className="text-3xl font-display font-bold mt-1.5" style={{ color: "#1B3A6B" }}>
          {value}
        </div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </div>
      <div
        className="h-10 w-10 rounded-md flex items-center justify-center"
        style={{ background: `${color}14`, color }}
      >
        <Icon size={18} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const filialCtx = useFilial();
  const filialSel = filialCtx?.selecionada || "";
  const [kpis, setKpis] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [loadingBriefing, setLoadingBriefing] = useState(false);

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setKpis(r.data));
  }, [filialSel]);

  const gerarBriefing = async () => {
    setLoadingBriefing(true);
    try {
      const { data } = await api.get("/ai/briefing-diretor");
      setBriefing(data.briefing);
    } finally {
      setLoadingBriefing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
            Olá, {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Visão consolidada da operação · {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
          </p>
        </div>
        {(user?.role === "diretor" || user?.role === "financeiro") && (
          <div className="flex gap-2">
            {user?.role === "diretor" && (
              <Button
                asChild
                variant="outline"
                data-testid="dashboard-ver-filiais"
                className="border-orange-200 text-orange-700 hover:bg-orange-50"
              >
                <Link to="/app/configuracoes/filiais">
                  <Building2 size={16} className="mr-2" />
                  Ver todas as filiais
                </Link>
              </Button>
            )}
            <Button
              data-testid="briefing-button"
              onClick={gerarBriefing}
              disabled={loadingBriefing}
              className="text-white"
              style={{ background: "#1B3A6B" }}
            >
              <Brain size={16} className="mr-2" />
              {loadingBriefing ? "Gerando briefing..." : "Briefing executivo (IA)"}
            </Button>
          </div>
        )}
      </div>

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            testId="kpi-mrr"
            icon={TrendingUp}
            label="MRR Atual"
            value={fmt(kpis.mrr)}
            hint={`${kpis.contratos_ativos} contratos ativos`}
            color="#F97316"
          />
          <KpiCard
            testId="kpi-frota"
            icon={Truck}
            label="Frota total"
            value={kpis.frota_total}
            hint={`${kpis.frota_locada} locadas · ${kpis.frota_disponivel} disponíveis`}
          />
          <KpiCard
            testId="kpi-tickets"
            icon={TicketCheck}
            label="Tickets abertos"
            value={kpis.tickets_abertos}
            hint="Triagem automática IA"
            color="#eab308"
          />
          <KpiCard
            testId="kpi-leads"
            icon={Users}
            label="Leads no funil"
            value={kpis.leads_funil}
            hint="Comercial ativo"
            color="#2563eb"
          />
          <KpiCard
            testId="kpi-disp"
            icon={Truck}
            label="Disponíveis"
            value={kpis.frota_disponivel}
            hint="Prontos para locar"
            color="#16a34a"
          />
          <KpiCard
            testId="kpi-locadas"
            icon={FileText}
            label="Locadas"
            value={kpis.frota_locada}
            hint="Em uso pelos clientes"
            color="#2563eb"
          />
          <KpiCard
            testId="kpi-oficina"
            icon={AlertTriangle}
            label="Na oficina"
            value={kpis.frota_oficina}
            hint="Em manutenção"
            color="#eab308"
          />
          <KpiCard
            testId="kpi-inad"
            icon={DollarSign}
            label="Inadimplência"
            value={fmt(kpis.inadimplencia)}
            hint="Em atraso"
            color="#dc2626"
          />
        </div>
      )}

      {/* Briefing IA */}
      {briefing && (
        <Card className="p-6 border-l-4" style={{ borderLeftColor: "#F97316" }} data-testid="briefing-output">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={18} style={{ color: "#F97316" }} />
            <h2 className="text-lg font-display font-bold">Briefing executivo · Claude</h2>
            <Badge variant="secondary" className="ml-auto text-[10px]">claude-sonnet-4-5</Badge>
          </div>
          <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed text-foreground/90">
            {briefing}
          </pre>
        </Card>
      )}
    </div>
  );
}
